import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast, toast } from '../components/UI/Toast';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import { Plus, Users, UserPlus, Briefcase, Trash2, RefreshCcw, Pencil } from 'lucide-react';
import { useTeams, TeamRole, Team } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 2rem;
  font-weight: 800;
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
`;

const SectionTitle = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.125rem;
  font-weight: 800;
  margin: 0 0 1rem 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Row = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 10px;
  background: ${props => props.theme.colors.surface};
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Meta = styled.div<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: ${p => (p.isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled.div<{ theme: any }>`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 12px;
  width: 520px;
  max-width: 92vw;
  overflow: hidden;
`;

const ModalHeader = styled.div<{ theme: any }>`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  font-weight: 800;
  color: ${p => p.theme.colors.text};
`;

const ModalBody = styled.div<{ theme: any }>`
  padding: 1rem 1.25rem;
  color: ${p => p.theme.colors.text};
`;

const ModalActions = styled.div<{ theme: any }>`
  padding: 0.75rem 1.25rem 1.25rem;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

const Field = styled.div<{ theme: any }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.875rem;
  label { color: ${p => p.theme.colors.textSecondary}; font-weight: 700; font-size: 0.85rem; }
  input, textarea {
    border: 1px solid ${p => p.theme.colors.border};
    background: ${p => p.theme.colors.background};
    color: ${p => p.theme.colors.text};
    border-radius: 8px;
    padding: 0.6rem 0.75rem;
    outline: none;
  }
`;

const TeamManagementPage: React.FC = () => {
  const { theme } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { user, currentUser } = useAuth();
  usePageTitle('Team Management');
  const {
    teams,
    teamMembers,
    teamClientAssignments,
    createTeam,
    deleteTeam,
    addMemberToTeam,
    updateTeamMember,
    removeMemberFromTeam,
    loadTeamMembers,
    loadTeamClients,
    refreshTeams,
    isLoadingTeams,
    isLoadingMembers,
    isLoadingAssignments,
    error,
  } = useTeams();

  const [createOpen, setCreateOpen] = useState(false);
  const [newTeam, setNewTeam] = useState<Pick<Team, 'name' | 'description'>>({ name: '', description: '' });
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{ teamId: string; memberId: string } | null>(null);
  const [editForm, setEditForm] = useState<{ firstName: string; lastName: string; role: TeamRole }>({ firstName: '', lastName: '', role: 'MEMBER' });
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addMemberMode, setAddMemberMode] = useState<'existing' | 'new'>('new');
  const [existingMemberId, setExistingMemberId] = useState<string>('');
  const [newMember, setNewMember] = useState<{ firstName: string; lastName: string; email: string; role: TeamRole }>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'MEMBER',
  });

  const isOwnerRole = useMemo(() => {
    const roleFromMe = currentUser?.role;
    if (typeof roleFromMe === 'string' && roleFromMe.trim()) {
      return roleFromMe.trim().toUpperCase() === 'OWNER';
    }

    const p: any = user?.profile || {};
    const direct = [
      p?.role,
      p?.teamRole,
      p?.team_role,
      p?.userRole,
      p?.user_role,
      p?.portalRole,
      p?.portal_role,
    ].find((v) => typeof v === 'string' && v.trim());
    if (direct) return String(direct).toUpperCase() === 'OWNER';
    const roles = Array.isArray(p?.roles) ? p.roles : null;
    if (roles && roles.some((r: any) => String(r).toUpperCase() === 'OWNER')) return true;
    return false;
  }, [currentUser?.role, user?.profile]);

  useEffect(() => {
    if (!isOwnerRole) return;
    if (selectedTeamId && teams.some(t => t.id === selectedTeamId)) return;
    setSelectedTeamId(teams[0]?.id || '');
  }, [isOwnerRole, selectedTeamId, teams]);

  useEffect(() => {
    if (!selectedTeamId) return;
    if (!isOwnerRole) return;
    void loadTeamMembers(selectedTeamId);
    void loadTeamClients(selectedTeamId);
  }, [isOwnerRole, selectedTeamId, loadTeamMembers, loadTeamClients]);

  const selectedTeamName = teams.find(t => t.id === selectedTeamId)?.name?.trim().toLowerCase();
  const isGeneralTeamSelected = selectedTeamName === 'general';

  const selectedTeamMemberEmails = useMemo(() => {
    return new Set(teamMembers.filter(m => m.teamId === selectedTeamId).map(m => m.email.toLowerCase()));
  }, [selectedTeamId, teamMembers]);

  const existingMemberDirectory = useMemo(() => {
    // Build a de-duplicated directory by email from whatever members we've loaded so far.
    // We exclude emails already in the selected team to reduce confusion.
    const map = new Map<string, { memberId: string; email: string; firstName?: string | null; lastName?: string | null }>();
    for (const m of teamMembers) {
      const email = (m.email || '').toLowerCase();
      if (!email) continue;
      if (m.teamId === selectedTeamId) continue;
      if (selectedTeamMemberEmails.has(email)) continue;
      if (!map.has(email)) {
        map.set(email, { memberId: m.id, email: m.email, firstName: m.firstName, lastName: m.lastName });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.email.localeCompare(b.email));
  }, [selectedTeamId, selectedTeamMemberEmails, teamMembers]);

  useEffect(() => {
    if (!addMemberOpen) return;
    // Reset mode defaults when opening
    setAddMemberMode('new');
    setExistingMemberId('');
  }, [addMemberOpen]);

  useEffect(() => {
    if (!addMemberOpen) return;
    if (addMemberMode !== 'existing') return;
    if (!isOwnerRole) return;
    // To support "existing member", we may need members from other teams.
    // Load members for all teams in the background.
    void (async () => {
      try {
        await Promise.all(teams.map(t => loadTeamMembers(t.id)));
      } catch {
        // errors are surfaced via context error/toasts in UI actions
      }
    })();
  }, [addMemberMode, addMemberOpen, isOwnerRole, loadTeamMembers, teams]);

  if (!isOwnerRole) {
    return (
      <Container>
        <Card>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{ fontWeight: 900, fontSize: '1.25rem', color: theme.colors.text, marginBottom: '0.5rem' }}>
              Access Denied
            </div>
            <div style={{ color: theme.colors.textSecondary, fontWeight: 600, marginBottom: '1.25rem' }}>
              You do not have permission to access Team Management.
            </div>
            <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </div>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <HeaderRow>
        <Title theme={theme}>Team Management</Title>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <Button variant="outline" onClick={() => void refreshTeams()} disabled={isLoadingTeams}>
            <RefreshCcw size={16} style={{ marginRight: 8 }} />
            {isLoadingTeams ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus size={16} style={{ marginRight: 8 }} />
            Create Team
          </Button>
        </div>
      </HeaderRow>

      <Grid>
        <Card>
          <SectionTitle theme={theme}><Users size={16} style={{ marginRight: 8 }} /> Teams</SectionTitle>
          {error && (
            <Meta theme={theme} style={{ marginBottom: '0.75rem', color: theme.colors.error }}>
              {error}
            </Meta>
          )}
          <List>
            {teams.map(t => (
              <Row key={t.id} theme={theme}>
                <Left>
                  <Briefcase size={18} />
                  <div>
                    <div style={{ fontWeight: 800 }}>{t.name}</div>
                    <Meta theme={theme}>
                      {t.description || 'No description'}
                    </Meta>
                  </div>
                </Left>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Meta theme={theme}>
                    {(t.membersCount ?? teamMembers.filter(m => m.teamId === t.id).length)} members · {(t.clientsCount ?? teamClientAssignments.filter(a => a.teamId === t.id).length)} clients
                  </Meta>
                  <Button
                    variant="outline"
                    size="small"
                    disabled={isLoadingTeams || !!t.isProtected || t.name?.trim().toLowerCase() === 'general'}
                    onClick={() => {
                      setDeleteTarget({ id: t.id, name: t.name });
                      setDeleteOpen(true);
                    }}
                  >
                    <Trash2 size={14} /> Delete
                  </Button>
                  <Button variant="outline" size="small" onClick={() => setSelectedTeamId(t.id)}>
                    Manage
                  </Button>
                </div>
              </Row>
            ))}
            {teams.length === 0 && (
              <Meta theme={theme}>
                {isLoadingTeams ? 'Loading teams...' : 'No teams yet. Create your first team to get started.'}
              </Meta>
            )}
          </List>
        </Card>

        <Card>
          <SectionTitle theme={theme}><UserPlus size={16} style={{ marginRight: 8 }} /> Members</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <label style={{ color: theme.colors.textSecondary, fontWeight: 700, fontSize: '0.85rem' }}>Team</label>
            <select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              style={{ padding: '0.5rem', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: theme.colors.background, color: theme.colors.text }}
            >
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <Button onClick={() => setAddMemberOpen(true)} disabled={!selectedTeamId}>
              <Plus size={16} style={{ marginRight: 6 }} /> Add Member
            </Button>
          </div>
          <List>
            {isLoadingMembers && selectedTeamId && (
              <Meta theme={theme}>Loading members...</Meta>
            )}
            {teamMembers.filter(m => m.teamId === selectedTeamId).map(m => (
              <Row key={m.id} theme={theme}>
                <Left>
                  <Users size={18} />
                  <div>
                    <div style={{ fontWeight: 800 }}>
                      {[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}
                    </div>
                    <Meta theme={theme}>
                      {m.email} · {m.role}{m.status ? ` · ${m.status}` : ''}
                    </Meta>
                  </div>
                </Left>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Button
                    variant="outline"
                    size="small"
                    disabled={isLoadingMembers}
                    onClick={() => {
                      setEditTarget({ teamId: selectedTeamId, memberId: m.id });
                      setEditForm({
                        firstName: (m.firstName || '') as string,
                        lastName: (m.lastName || '') as string,
                        role: m.role,
                      });
                      setEditOpen(true);
                    }}
                  >
                    <Pencil size={14} /> Edit
                  </Button>
                <Button
                  variant="outline"
                  size="small"
                  disabled={isLoadingMembers || (isGeneralTeamSelected && m.role === 'OWNER')}
                  onClick={async () => {
                    try {
                      if (isGeneralTeamSelected && m.role === 'OWNER') {
                        showToast(toast.info('Not allowed', 'The owner of the General team cannot be removed.'));
                        return;
                      }
                      await removeMemberFromTeam(selectedTeamId, m.id);
                      showToast(toast.success('Removed', 'Member removed from team.'));
                    } catch (e: any) {
                      showToast(toast.error('Remove failed', e?.message || 'Unable to remove member.'));
                    }
                  }}
                >
                  <Trash2 size={14} /> Remove
                </Button>
                </div>
              </Row>
            ))}
            {teamMembers.filter(m => m.teamId === selectedTeamId).length === 0 && (
              <Meta theme={theme}>No members in this team.</Meta>
            )}
          </List>
        </Card>

        <Card>
          <SectionTitle theme={theme}><UserPlus size={16} style={{ marginRight: 8 }} /> Assignments</SectionTitle>
          <List>
            {isLoadingAssignments && selectedTeamId && (
              <Meta theme={theme}>Loading assignments...</Meta>
            )}
            {teamClientAssignments.filter(a => a.teamId === selectedTeamId).length === 0 && (
              <Meta theme={theme}>No client-team assignments yet.</Meta>
            )}
            {teamClientAssignments.filter(a => a.teamId === selectedTeamId).map(a => {
              return (
                <Row key={a.id} theme={theme}>
                  <Left>
                    <Users size={18} />
                    <div>
                      <div style={{ fontWeight: 800 }}>{a.client?.companyName || a.client?.email || a.client?.id}</div>
                      <Meta theme={theme}>
                        Role: {a.role}
                        {a.client?.tin ? ` · Tax ID: ${a.client.tin}` : ''}
                        {a.client?.cacNumber ? ` · CAC: ${a.client.cacNumber}` : ''}
                      </Meta>
                    </div>
                  </Left>
                  <Meta theme={theme}>{new Date(a.createdAt).toLocaleString()}</Meta>
                </Row>
              );
            })}
          </List>
        </Card>
      </Grid>

      <ModalOverlay isOpen={createOpen}>
        <ModalCard theme={theme}>
          <ModalHeader theme={theme}>Create Team</ModalHeader>
          <ModalBody theme={theme}>
            <Field theme={theme}>
              <label>Team Name</label>
              <input value={newTeam.name} onChange={(e) => setNewTeam(v => ({ ...v, name: e.target.value }))} placeholder="e.g., Corporate Tax Team" />
            </Field>
            <Field theme={theme}>
              <label>Description</label>
              <textarea
                rows={3}
                value={newTeam.description ?? ''}
                onChange={(e) => setNewTeam(v => ({ ...v, description: e.target.value }))}
                placeholder="Short description"
              />
            </Field>
          </ModalBody>
          <ModalActions theme={theme}>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              disabled={isLoadingTeams}
              onClick={async () => {
                if (!newTeam.name.trim()) return;
                try {
                  const created = await createTeam(newTeam);
                  showToast(toast.success('Team created', 'Your team has been created.'));
                  setCreateOpen(false);
                  setNewTeam({ name: '', description: '' });
                  if (created?.id) {
                    setSelectedTeamId(created.id);
                    setAddMemberOpen(true);
                  }
                } catch (e: any) {
                  showToast(toast.error('Create failed', e?.message || 'Unable to create team.'));
                }
              }}
            >
              {isLoadingTeams ? 'Creating...' : 'Create'}
            </Button>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>

      <ModalOverlay isOpen={addMemberOpen}>
        <ModalCard theme={theme}>
          <ModalHeader theme={theme}>Add Member</ModalHeader>
          <ModalBody theme={theme}>
            <Field theme={theme}>
              <label>Add member from</label>
              <select
                value={addMemberMode}
                onChange={(e) => setAddMemberMode(e.target.value as any)}
                style={{ padding: '0.6rem 0.75rem', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: theme.colors.background, color: theme.colors.text }}
              >
                <option value="new">New member</option>
                <option value="existing">Existing member</option>
              </select>
            </Field>

            {addMemberMode === 'existing' ? (
              <>
                <Field theme={theme}>
                  <label>Existing Member</label>
                  <select
                    value={existingMemberId}
                    onChange={(e) => setExistingMemberId(e.target.value)}
                    style={{ padding: '0.6rem 0.75rem', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: theme.colors.background, color: theme.colors.text }}
                  >
                    <option value="" disabled>Select a member</option>
                    {existingMemberDirectory.map((m) => (
                      <option key={m.memberId} value={m.memberId}>
                        {([m.firstName, m.lastName].filter(Boolean).join(' ') || m.email)} ({m.email})
                      </option>
                    ))}
                  </select>
                </Field>
                {existingMemberDirectory.length === 0 && (
                  <div style={{ color: theme.colors.textSecondary, fontWeight: 600, marginTop: '-0.25rem' }}>
                    No existing members found yet. Switch to “New member” or ensure other teams have members.
                  </div>
                )}
              </>
            ) : (
              <>
                <Field theme={theme}>
                  <label>First Name</label>
                  <input value={newMember.firstName} onChange={(e) => setNewMember(v => ({ ...v, firstName: e.target.value }))} placeholder="e.g., Jane" />
                </Field>
                <Field theme={theme}>
                  <label>Last Name</label>
                  <input value={newMember.lastName} onChange={(e) => setNewMember(v => ({ ...v, lastName: e.target.value }))} placeholder="e.g., Doe" />
                </Field>
                <Field theme={theme}>
                  <label>Email</label>
                  <input type="email" value={newMember.email} onChange={(e) => setNewMember(v => ({ ...v, email: e.target.value }))} placeholder="name@example.com" />
                </Field>
              </>
            )}
            <Field theme={theme}>
              <label>Role</label>
              <select
                value={newMember.role}
                onChange={(e) => setNewMember(v => ({ ...v, role: e.target.value as TeamRole }))}
                style={{ padding: '0.6rem 0.75rem', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: theme.colors.background, color: theme.colors.text }}
              >
                <option value="OWNER">Owner</option>
                <option value="MEMBER">Member</option>
              </select>
            </Field>
          </ModalBody>
          <ModalActions theme={theme}>
            <Button variant="outline" onClick={() => setAddMemberOpen(false)}>Cancel</Button>
            <Button
              disabled={isLoadingMembers}
              onClick={async () => {
                if (!selectedTeamId) return;
                try {
                  if (addMemberMode === 'existing') {
                    if (!existingMemberId) return;
                    await addMemberToTeam(selectedTeamId, { memberId: existingMemberId, role: newMember.role });
                  } else {
                    if (!newMember.email.trim()) return;
                    await addMemberToTeam(selectedTeamId, {
                      email: newMember.email.trim(),
                      firstName: newMember.firstName.trim() || undefined,
                      lastName: newMember.lastName.trim() || undefined,
                      role: newMember.role,
                    });
                  }
                  showToast(toast.success('Invite sent', 'Team member has been invited.'));
                  setAddMemberOpen(false);
                  setNewMember({ firstName: '', lastName: '', email: '', role: 'MEMBER' });
                  setExistingMemberId('');
                } catch (e: any) {
                  showToast(toast.error('Invite failed', e?.message || 'Unable to invite member.'));
                }
              }}
            >
              {isLoadingMembers ? 'Adding...' : 'Add'}
            </Button>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>

      <ModalOverlay isOpen={deleteOpen}>
        <ModalCard theme={theme}>
          <ModalHeader theme={theme}>Delete Team</ModalHeader>
          <ModalBody theme={theme}>
            <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>
              {deleteTarget?.name || 'This team'}
            </div>
            <div style={{ color: theme.colors.textSecondary, fontWeight: 600 }}>
              This will permanently delete the team and remove its member/client assignments. This action cannot be undone.
            </div>
          </ModalBody>
          <ModalActions theme={theme}>
            <Button variant="outline" onClick={() => { setDeleteOpen(false); setDeleteTarget(null); }}>
              Cancel
            </Button>
            <Button
              disabled={isLoadingTeams || !deleteTarget}
              onClick={async () => {
                if (!deleteTarget) return;
                try {
                  await deleteTeam(deleteTarget.id);
                  showToast(toast.success('Team deleted', `"${deleteTarget.name}" has been deleted.`));
                  // Selection will auto-adjust via effect when teams updates
                  if (selectedTeamId === deleteTarget.id) {
                    setSelectedTeamId('');
                  }
                  setDeleteOpen(false);
                  setDeleteTarget(null);
                } catch (e: any) {
                  showToast(toast.error('Delete failed', e?.message || 'Unable to delete team.'));
                }
              }}
            >
              {isLoadingTeams ? 'Deleting...' : 'Delete'}
            </Button>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>

      <ModalOverlay isOpen={editOpen}>
        <ModalCard theme={theme}>
          <ModalHeader theme={theme}>Edit Member</ModalHeader>
          <ModalBody theme={theme}>
            <Field theme={theme}>
              <label>First Name</label>
              <input value={editForm.firstName} onChange={(e) => setEditForm(v => ({ ...v, firstName: e.target.value }))} placeholder="First name" />
            </Field>
            <Field theme={theme}>
              <label>Last Name</label>
              <input value={editForm.lastName} onChange={(e) => setEditForm(v => ({ ...v, lastName: e.target.value }))} placeholder="Last name" />
            </Field>
            <Field theme={theme}>
              <label>Role</label>
              <select
                value={editForm.role}
                onChange={(e) => setEditForm(v => ({ ...v, role: e.target.value as TeamRole }))}
                style={{ padding: '0.6rem 0.75rem', borderRadius: 8, border: `1px solid ${theme.colors.border}`, background: theme.colors.background, color: theme.colors.text }}
                disabled={(() => {
                  if (!editTarget) return false;
                  const teamName = teams.find(t => t.id === editTarget.teamId)?.name?.trim().toLowerCase();
                  if (teamName !== 'general') return false;
                  const member = teamMembers.find(m => m.teamId === editTarget.teamId && m.id === editTarget.memberId);
                  return member?.role === 'OWNER';
                })()}
              >
                <option value="OWNER">Owner</option>
                <option value="MEMBER">Member</option>
              </select>
              {(() => {
                if (!editTarget) return null;
                const teamName = teams.find(t => t.id === editTarget.teamId)?.name?.trim().toLowerCase();
                if (teamName !== 'general') return null;
                const member = teamMembers.find(m => m.teamId === editTarget.teamId && m.id === editTarget.memberId);
                if (member?.role !== 'OWNER') return null;
                return (
                  <div style={{ color: theme.colors.textSecondary, fontWeight: 600, marginTop: '0.5rem' }}>
                    The owner of the General team cannot have their role changed.
                  </div>
                );
              })()}
            </Field>
          </ModalBody>
          <ModalActions theme={theme}>
            <Button variant="outline" onClick={() => { setEditOpen(false); setEditTarget(null); }}>
              Cancel
            </Button>
            <Button
              disabled={isLoadingMembers || !editTarget}
              onClick={async () => {
                if (!editTarget) return;
                try {
                  await updateTeamMember(editTarget.teamId, editTarget.memberId, {
                    firstName: editForm.firstName.trim() || null,
                    lastName: editForm.lastName.trim() || null,
                    role: editForm.role,
                  });
                  showToast(toast.success('Updated', 'Member updated successfully.'));
                  setEditOpen(false);
                  setEditTarget(null);
                } catch (e: any) {
                  showToast(toast.error('Update failed', e?.message || 'Unable to update member.'));
                }
              }}
            >
              {isLoadingMembers ? 'Saving...' : 'Save'}
            </Button>
          </ModalActions>
        </ModalCard>
      </ModalOverlay>
    </Container>
  );
};

export default TeamManagementPage;


